/**
 * <upgrade-subscription>
 * Converts a one-time cart line to its subscription selling plan in place,
 * keeping the line's quantity, via POST /cart/change. The drawer is then
 * re-rendered from the Section Rendering API response — no page navigation,
 * no dependency on the theme's pub/sub being global.
 *
 * Guarded so the AJAX section re-render (which re-inserts this script tag but
 * does not re-execute it) never throws "already defined".
 */
if (!customElements.get('upgrade-subscription')) {
  class UpgradeSubscription extends HTMLElement {
    constructor() {
      super();
      this.button = this.querySelector('[data-upgrade-button]');
      if (this.button) {
        this.button.addEventListener('click', this.onClick.bind(this));
      }
    }

    onClick(event) {
      event.preventDefault();
      if (this.getAttribute('aria-busy') === 'true') return;

      const id = this.dataset.key;
      const sellingPlan = this.dataset.plan;
      const quantity = parseInt(this.dataset.quantity, 10) || 1;
      if (!id || !sellingPlan) return;

      const changeUrl = (window.routes && window.routes.cart_change_url) || '/cart/change';
      this.setAttribute('aria-busy', 'true');
      // base.css `.hidden` is display:none!important, so the aria-busy CSS
      // alone can't reveal the spinner — drop the class like product-form does.
      const spinner = this.querySelector('.loading__spinner');
      if (spinner) spinner.classList.remove('hidden');

      fetch(changeUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          id: id,
          quantity: quantity,
          selling_plan: sellingPlan,
          sections: 'cart-drawer,cart-icon-bubble',
          sections_url: window.location.pathname,
        }),
      })
        .then((response) => response.json())
        .then((cart) => {
          if (cart && cart.status) {
            this.fail(cart);
            return;
          }
          this.render(cart);
        })
        .catch(() => this.fail());
    }

    render(cart) {
      const sections = (cart && cart.sections) || {};

      // Re-render the open drawer's inner content (header count, free-ship bar,
      // line items, footer savings) without closing it.
      if (sections['cart-drawer']) {
        const parsed = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
        const nextInner = parsed.querySelector('.drawer__inner');
        const currentInner = document.querySelector('cart-drawer .drawer__inner');
        if (nextInner && currentInner) {
          currentInner.innerHTML = nextInner.innerHTML;
        }
      }

      // Update the header cart bubble (count/markup) if present.
      if (sections['cart-icon-bubble']) {
        const bubble = document.getElementById('cart-icon-bubble');
        if (bubble) {
          const parsed = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html');
          const nextBubble = parsed.getElementById('cart-icon-bubble');
          if (nextBubble) bubble.innerHTML = nextBubble.innerHTML;
        }
      }

      // Best-effort: notify any cart-update subscribers the theme may have.
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        try {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-upgrade', cartData: cart });
        } catch (e) {
          /* no-op */
        }
      }
    }

    fail(cart) {
      this.removeAttribute('aria-busy');
      const spinner = this.querySelector('.loading__spinner');
      if (spinner) spinner.classList.add('hidden');
      if (cart && (cart.message || cart.description)) {
        // eslint-disable-next-line no-console
        console.warn('[cart-upgrade] could not switch to subscription:', cart.message || cart.description);
      }
    }
  }

  customElements.define('upgrade-subscription', UpgradeSubscription);
}
